import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQueue } from '../hooks/useQueue';
import { useSettings } from '../contexts/SettingsContext';
import {
  UserPlus, ChevronsRight, Megaphone, AlertOctagon,
  Search, SlidersHorizontal, Check, X
} from 'lucide-react';
import { CopperButton } from '../components/ui/copper-button';
import { SlateButton } from '../components/ui/slate-button';
import { GlassButton } from '../components/ui/glass-button';
import { PatientSection } from '../components/receptionist/PatientSection';
import { AddPatientModal } from '../components/receptionist/AddPatientModal';
import { RoleShell } from '../components/RoleShell';
import { toast } from 'sonner';

export const ReceptionistView = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { settings } = useSettings();
  const { patients, loading, error, addPatient, addEmergency, updatePatient } = useQueue({ token });

  const [expandedRow, setExpandedRow] = useState(null);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const filterRef = useRef(null);
  const [collapsedSections, setCollapsedSections] = useState({
    'payment-pending': true,
    'completed': true,
    'no-show': true
  });

  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilterMenu(false);
      }
    };
    if (showFilterMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilterMenu]);

  const visiblePatients = patients.filter(p => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      (p.patient_name || '').toLowerCase().includes(q) ||
      (p.token_number || '').toLowerCase().includes(q) ||
      (p.patient_phone || '').toLowerCase().includes(q);
    const matchesFilter = activeFilters.length === 0 || activeFilters.includes(p.status);
    return matchesSearch && matchesFilter;
  });

  const groupedPatients = {
    'in-consultation': visiblePatients.filter(p => p.status === 'in-consultation'),
    'next-in-line':    visiblePatients.filter(p => p.status === 'next-in-line'),
    'waiting':         visiblePatients.filter(p => p.status === 'waiting'),
    'in-queue':        visiblePatients.filter(p => p.status === 'in-queue'),
    'payment-pending': visiblePatients.filter(p => p.status === 'payment-pending'),
    'completed':       visiblePatients.filter(p => p.status === 'completed'),
    'no-show':         visiblePatients.filter(p => p.status === 'no-show')
  };

  // KPIs always reflect the full dataset, not the filtered view.
  const stats = {
    inQueue: patients.filter(p => p.status === 'in-queue').length,
    waiting: patients.filter(p => p.status === 'waiting' || p.status === 'next-in-line').length,
    inConsultation: patients.filter(p => p.status === 'in-consultation').length,
    completed: patients.filter(p => p.status === 'completed').length
  };

  const handleStatusChange = async (patientId, newStatus) => {
    try {
      await updatePatient(patientId, { status: newStatus });
      toast.success('Status updated');
    } catch (e) {
      toast.error('Could not update status');
    }
  };

  const handleAddPatient = async (data) => {
    try {
      const newP = await addPatient({
        patient_name: data.patient_name,
        patient_phone: data.patient_phone,
        entry_method: data.entry_method,
        chief_complaint: data.chief_complaint,
      });
      setShowAddPatientModal(false);
      toast.success(`${newP.token_number} added to queue`);
    } catch (e) {
      toast.error('Could not add patient');
    }
  };

  const handleAddEmergency = async (data) => {
    try {
      const ep = await addEmergency({
        patient_name: data.name,
        patient_phone: data.phone,
        chief_complaint: data.complaint || 'Emergency case',
      });
      setShowEmergencyModal(false);
      toast.error(`EMERGENCY · ${ep.token_number} · ${data.name} added to top of queue`, { duration: 5000 });
    } catch (e) {
      toast.error('Could not add emergency patient');
    }
  };

  const toggleFilter = (status) => {
    setActiveFilters(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleCallNext = async () => {
    const nextWaiting = patients.find(p => p.status === 'waiting');
    if (nextWaiting) {
      await handleStatusChange(nextWaiting.id, 'next-in-line');
      toast.success(`${nextWaiting.token_number} · ${nextWaiting.patient_name} is next`);
    } else {
      toast('No one is currently waiting');
    }
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', day: 'numeric', month: 'long'
  });

  return (
    <RoleShell
      titlePrefix="Reception"
      userName={user?.name || 'Sunita Patil'}
      isLive={!error}
    >
      <div className="flex" style={{ height: 'calc(100vh - 68px)' }} data-testid="receptionist-view">
        {/* Sidebar */}
        <div className="glass-primary overflow-y-auto" style={{ width: '300px', padding: '24px' }}>
          <div className="mb-7">
            <div className="text-overline mb-3">TODAY AT A GLANCE</div>
            <div className="grid grid-cols-2 gap-2.5">
              <StatCard label="In Queue" value={stats.inQueue} />
              <StatCard label="Waiting" value={stats.waiting} />
              <StatCard label="In Consultation" value={stats.inConsultation} />
              <StatCard label="Completed" value={stats.completed} />
            </div>
          </div>

          <div className="mb-7">
            <div className="text-overline mb-3">QUICK ACTIONS</div>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => setShowAddPatientModal(true)}
                className="btn-copper-3d h-[48px] w-full rounded-[var(--radius-lg)] font-['Sora'] text-[15px] font-semibold flex items-center justify-center gap-2"
                data-testid="add-patient-btn"
              >
                <UserPlus size={16} strokeWidth={2} />
                Add Patient
              </button>

              <button
                onClick={handleCallNext}
                className="btn-slate-3d h-[48px] w-full rounded-[var(--radius-lg)] font-['Sora'] text-[15px] font-semibold flex items-center justify-center gap-2"
                data-testid="call-next-btn"
              >
                <ChevronsRight size={16} strokeWidth={2} />
                Call Next
              </button>

              <button
                onClick={() => toast('Broadcast delay (demo)')}
                className="btn-glass-3d h-[48px] w-full rounded-[var(--radius-lg)] font-['Sora'] text-[15px] font-semibold flex items-center justify-center gap-2"
                data-testid="broadcast-btn"
              >
                <Megaphone size={16} strokeWidth={2} />
                Broadcast Delay
              </button>

              <button
                onClick={() => setShowEmergencyModal(true)}
                className="btn-danger-3d h-[48px] w-full rounded-[var(--radius-lg)] font-['Sora'] text-[15px] font-semibold flex items-center justify-center gap-2"
                data-testid="emergency-btn"
              >
                <AlertOctagon size={16} strokeWidth={2} />
                Emergency
              </button>
            </div>
          </div>

          {groupedPatients['in-consultation'].length > 0 && (
            <InConsultationCard patient={groupedPatients['in-consultation'][0]} />
          )}
        </div>

        {/* Main area */}
        <div className="flex-1 overflow-y-auto" style={{ background: 'transparent', padding: '32px 28px' }}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="font-['Sora'] text-[26px] font-bold" style={{ color: '#1C1917' }}>
                Today, {currentDate}
              </h2>
              <div
                className="px-3 py-1 rounded-full font-['Sora'] text-[12px] font-semibold"
                style={{
                  background: 'rgba(61, 90, 128, 0.08)',
                  border: '1px solid rgba(61, 90, 128, 0.18)',
                  color: 'var(--color-slate)'
                }}
              >
                {patients.length} patients
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="glass-subtle flex items-center gap-2 px-3.5 rounded-[12px]" style={{ width: '280px', height: '42px' }}>
                <Search size={16} strokeWidth={1.75} style={{ color: 'var(--color-slate)', opacity: 0.6 }} />
                <input
                  type="text"
                  placeholder="Search by name, phone or token"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none font-['Inter'] text-[15px]"
                  style={{ color: '#1C1917' }}
                  data-testid="search-input"
                />
              </div>

              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                  className="h-[42px] px-4 rounded-[12px] font-['Sora'] text-[15px] font-medium flex items-center gap-2 transition-all duration-180 glass-subtle hover:bg-[rgba(61,90,128,0.06)]"
                  style={{ color: activeFilters.length > 0 ? 'var(--color-slate)' : '#44403C' }}
                  data-testid="filter-btn"
                >
                  <SlidersHorizontal size={16} strokeWidth={1.75} />
                  Filter
                  {activeFilters.length > 0 && (
                    <span className="ml-1 px-1.5 rounded-full font-['DM_Mono'] text-[11px] font-semibold" style={{ background: 'var(--color-slate)', color: 'white' }}>
                      {activeFilters.length}
                    </span>
                  )}
                </button>

                {showFilterMenu && (
                  <div
                    className="absolute right-0 top-full mt-2 rounded-[var(--radius-md)] p-3 z-[100] animate-slide-up"
                    style={{
                      minWidth: '240px',
                      background: '#FFFFFF',
                      backgroundColor: '#FFFFFF',
                      isolation: 'isolate',
                      backdropFilter: 'none',
                      WebkitBackdropFilter: 'none',
                      border: '1px solid rgba(28, 25, 23, 0.12)',
                      boxShadow: '0 24px 48px rgba(28, 25, 23, 0.18)'
                    }}
                    data-testid="filter-menu"
                  >
                    <div className="text-overline mb-2" style={{ color: '#78716C' }}>FILTER BY STATUS</div>
                    {['in-queue','waiting','next-in-line','in-consultation','payment-pending','completed','no-show'].map(status => {
                      const checked = activeFilters.includes(status);
                      return (
                        <button
                          key={status}
                          onClick={() => toggleFilter(status)}
                          className="w-full flex items-center justify-between px-2 py-1.5 rounded-[var(--radius-sm)] hover:bg-[rgba(61,90,128,0.06)] transition-colors duration-180"
                          data-testid={`filter-option-${status}`}
                        >
                          <span className="font-['Inter'] text-sm capitalize" style={{ color: '#1C1917' }}>
                            {status.replace(/-/g, ' ')}
                          </span>
                          {checked && <Check size={14} strokeWidth={2} style={{ color: 'var(--color-slate)' }} />}
                        </button>
                      );
                    })}
                    {activeFilters.length > 0 && (
                      <button
                        onClick={() => { setActiveFilters([]); setShowFilterMenu(false); }}
                        className="mt-2 w-full px-2 py-1.5 rounded-[var(--radius-sm)] font-['Sora'] text-xs font-semibold transition-colors duration-180 hover:bg-[rgba(185,28,28,0.06)]"
                        style={{ color: '#B91C1C' }}
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="glass-primary rounded-[var(--radius-lg)] overflow-hidden">
            <div
              className="flex items-center px-5"
              style={{ height: '40px', background: 'rgba(28, 25, 23, 0.02)', borderBottom: '1px solid rgba(28, 25, 23, 0.08)' }}
            >
              <div className="text-overline" style={{ width: '80px' }}>TOKEN</div>
              <div className="text-overline" style={{ width: '160px' }}>STATUS</div>
              <div className="text-overline flex-1" style={{ minWidth: '220px' }}>PATIENT</div>
              <div className="text-overline" style={{ width: '120px' }}>ENTRY</div>
              <div className="text-overline text-right" style={{ width: '120px' }}>TIMER</div>
              <div className="text-overline text-right" style={{ width: '140px' }}>ACTIONS</div>
            </div>

            {loading && patients.length === 0 && (
              <div className="px-5 py-10 text-center font-['Inter'] text-sm" style={{ color: '#78716C' }}>
                Loading queue…
              </div>
            )}

            {!loading && patients.length === 0 && (
              <div className="px-5 py-10 text-center font-['Inter'] text-sm" style={{ color: '#78716C' }}>
                No patients yet today. Click "Add Patient" to start.
              </div>
            )}

            <PatientSection title="IN CONSULTATION" patients={groupedPatients['in-consultation']} collapsed={false} onToggle={() => {}} expandedRow={expandedRow} onRowClick={setExpandedRow} onStatusChange={handleStatusChange} />
            <PatientSection title="NEXT IN LINE"    patients={groupedPatients['next-in-line']}    collapsed={false} onToggle={() => {}} expandedRow={expandedRow} onRowClick={setExpandedRow} onStatusChange={handleStatusChange} />
            <PatientSection title="WAITING"         patients={groupedPatients['waiting']}         collapsed={false} onToggle={() => {}} expandedRow={expandedRow} onRowClick={setExpandedRow} onStatusChange={handleStatusChange} />
            <PatientSection title="IN QUEUE"        patients={groupedPatients['in-queue']}        collapsed={false} onToggle={() => {}} expandedRow={expandedRow} onRowClick={setExpandedRow} onStatusChange={handleStatusChange} />
            <PatientSection title="PAYMENT PENDING" patients={groupedPatients['payment-pending']} collapsed={collapsedSections['payment-pending']} onToggle={() => toggleSection('payment-pending')} expandedRow={expandedRow} onRowClick={setExpandedRow} onStatusChange={handleStatusChange} />
            <PatientSection title="COMPLETED"       patients={groupedPatients['completed']}       collapsed={collapsedSections['completed']}       onToggle={() => toggleSection('completed')}       expandedRow={expandedRow} onRowClick={setExpandedRow} onStatusChange={handleStatusChange} />
            <PatientSection title="NO SHOW"         patients={groupedPatients['no-show']}         collapsed={collapsedSections['no-show']}         onToggle={() => toggleSection('no-show')}         expandedRow={expandedRow} onRowClick={setExpandedRow} onStatusChange={handleStatusChange} />
          </div>
        </div>
      </div>

      {showAddPatientModal && (
        <AddPatientModal
          onClose={() => setShowAddPatientModal(false)}
          onSubmit={handleAddPatient}
        />
      )}

      {showEmergencyModal && (
        <EmergencyModal
          onClose={() => setShowEmergencyModal(false)}
          onSubmit={handleAddEmergency}
        />
      )}
    </RoleShell>
  );
};

const StatCard = ({ label, value }) => (
  <div className="glass-primary rounded-[var(--radius-md)] p-4 flex flex-col gap-1">
    <div className="font-['Inter'] text-[13px]" style={{ color: '#78716C' }}>{label}</div>
    <div className="font-['DM_Mono'] text-[30px] font-medium leading-none" style={{ color: '#1C1917' }}>{value}</div>
  </div>
);

const InConsultationCard = ({ patient }) => {
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (patient.consultation_started_at) {
      const tick = () => {
        const start = new Date(patient.consultation_started_at);
        setDuration(Math.floor((new Date() - start) / 1000));
      };
      tick();
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }
  }, [patient]);

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="glass-elevated rounded-[var(--radius-md)] p-5 mb-5" style={{ borderLeft: '3px solid var(--color-slate)' }}>
      <div className="text-overline mb-2" style={{ color: 'var(--color-slate)' }}>IN CONSULTATION</div>
      <div className="font-['Sora'] text-xl font-semibold mb-0.5" style={{ color: '#1C1917' }}>{patient.patient_name}</div>
      <div className="font-['DM_Mono'] text-[14px] mb-3" style={{ color: '#78716C' }}>{patient.token_number}</div>
      <div className="rounded-[10px] p-3 mb-2" style={{ background: 'rgba(61, 90, 128, 0.06)' }}>
        <div className="text-overline mb-1" style={{ color: '#78716C', fontSize: '10px' }}>DURATION</div>
        <div className="font-['DM_Mono'] text-[24px] font-medium" style={{ color: 'var(--color-slate)' }}>{formatDuration(duration)}</div>
      </div>
    </div>
  );
};

const EmergencyModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ name: '', phone: '', complaint: '' });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    onSubmit(form);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(28, 25, 23, 0.55)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="emergency-modal"
    >
      <div
        className="rounded-[var(--radius-2xl)] p-8 w-full max-w-md animate-slide-up"
        style={{ background: '#FFFFFF', border: '2px solid rgba(185, 28, 28, 0.25)', boxShadow: '0 30px 60px rgba(185, 28, 28, 0.18)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center animate-pulse" style={{ background: 'rgba(185, 28, 28, 0.10)' }}>
              <AlertOctagon size={20} strokeWidth={2} style={{ color: '#B91C1C' }} />
            </div>
            <div>
              <h2 className="font-['Sora'] text-xl font-bold" style={{ color: '#B91C1C' }}>Emergency Intake</h2>
              <p className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>Patient will be placed at the top of the queue</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full glass-subtle flex items-center justify-center hover:bg-[rgba(185,28,28,0.08)] transition-all duration-180">
            <X size={16} strokeWidth={1.75} style={{ color: '#44403C' }} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-overline block mb-2">PATIENT NAME *</label>
            <input
              type="text" required autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Enter patient name"
              className="w-full glass-subtle rounded-[var(--radius-md)] h-[52px] px-4 font-['Inter'] text-base outline-none"
              style={{ color: '#1C1917' }}
              data-testid="emergency-name-input"
            />
          </div>

          <div>
            <label className="text-overline block mb-2">PHONE NUMBER *</label>
            <div className="glass-subtle rounded-[var(--radius-md)] h-[52px] flex items-center px-4">
              <span className="font-['DM_Mono'] text-sm" style={{ color: '#78716C' }}>+91</span>
              <div className="w-px h-6 mx-3" style={{ backgroundColor: 'rgba(28, 25, 23, 0.12)' }} />
              <input
                type="tel" required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="98765 43210"
                className="flex-1 bg-transparent border-none outline-none font-['Inter'] text-base"
                style={{ color: '#1C1917' }}
                data-testid="emergency-phone-input"
              />
            </div>
          </div>

          <div>
            <label className="text-overline block mb-2">NATURE OF EMERGENCY</label>
            <textarea
              value={form.complaint}
              onChange={(e) => setForm({ ...form, complaint: e.target.value })}
              placeholder="e.g. Chest pain, severe injury, breathing difficulty..."
              className="w-full glass-subtle rounded-[var(--radius-md)] p-4 font-['Inter'] text-sm outline-none resize-none"
              style={{ color: '#1C1917', minHeight: '90px' }}
              data-testid="emergency-complaint-input"
            />
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 h-12 rounded-[var(--radius-lg)] glass-subtle font-['Sora'] font-semibold transition-colors duration-180 hover:bg-[rgba(28,25,23,0.04)]" style={{ color: '#44403C' }}>
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 h-12 rounded-[var(--radius-lg)] font-['Sora'] font-semibold text-white"
              style={{
                background: 'linear-gradient(180deg, #EF4444 0%, #DC2626 35%, #B91C1C 80%, #991B1B 100%)',
                border: '1px solid rgba(255,255,255,0.15)',
                boxShadow: '0 1px 0 0 rgba(255,255,255,0.35) inset, 0 -1px 0 0 rgba(0,0,0,0.18) inset, 0 12px 24px -8px rgba(185, 28, 28, 0.40), 0 4px 8px -2px rgba(185, 28, 28, 0.25)',
                textShadow: '0 1px 0 rgba(80, 10, 10, 0.30)',
                transition: 'transform 200ms ease, filter 200ms ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.filter = 'brightness(1.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.filter = ''; }}
              data-testid="emergency-submit-btn"
            >
              Add to Top of Queue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReceptionistView;
